<script setup lang="ts">
import SportsBook from "@/components/layout/Sport/SportsBook/index.vue";
import SportsBookOutrights from "@/components/layout/Sport/SportsBookOutrights/index.vue";
import { useQuery } from "@tanstack/vue-query";
import BetbookSignalRCallback from "~/composables/SignalR/callbacks/BetbookSignalRCallback";
import { signalREndpoints, useSignalR } from "~/composables/SignalR/useSignalR";
import useSignalRRecconect from "~/composables/useSignalRRecconect";
import { useBetbook } from "~/store/betbook";
import { useSettings } from "~/store/settings";
import { IEvent } from "~/types/sports/event";
import { IMarket } from "~/types/sports/market";
import { IOutcome } from "~/types/sports/outcome";
import { IPrice } from "~/types/sports/price";
import { IRegion } from "~/types/sports/region";
import { IScore } from "~/types/sports/score";
import { ISport } from "~/types/sports/sport";
import api from "~/utils/auth/api";
import { totalGoalMarkets } from "~/utils/constants";

interface Props {
	sport: ISport | undefined;
	isEsports: boolean;
	fixtureType: string;
}

const props = defineProps<Props>();

const fixtureTypeMap: { [x: string]: number } = {
	live: 0,
	highlights: 1,
	upcoming: 2,
	filter: 3,
	outrights: 4,
	betwayboost: 5,
};

const settings = useSettings();
const { $checkSignalRConnectionStatus } = useNuxtApp();
const store = useBetbook();
const shouldFetchBook = computed(() => !!props.sport && !store.applyingFilters);
const reactiveFilters = computed(() => store.filters);
const reactiveDisplayMarkets = computed(() => store.displayMarkets);
const filterApplied = computed(
	() =>
		props.fixtureType === "highlights" &&
		(store.filters.sortOrder !== "League" ||
			!!store.filters.regionAndLeagueIds ||
			!!store.filters.fromStartEpoch ||
			!!store.filters.toStartEpoch ||
			!!store.filters.odds)
);
const betbookSignalR = useSignalR("betBook", BetbookSignalRCallback);
const totalGoalsIsActive = computed(() => !!reactiveDisplayMarkets.value.find((m) => m.name.includes("Total Goals")));
const ledger = useLedger();

export interface IBetbookCall {
	isFinalPage: boolean;
	isFinalPageFiltered: boolean;
	events: IEvent[];
	markets: IMarket[];
	outcomes: IOutcome[];
	outrightRegions: {
		name: string;
		regionId: string;
		sportId: string;
		sportEvents: IEvent[];
	}[];
	prices: IPrice[];
	scores: IScore[];
	regions: IRegion[];
}

const handleSignalRunsubscribe = () => {
	const ledgerData = ledger.get("events", "betBook");
	if (ledgerData) {
		betbookSignalR.unSubscribe(signalREndpoints.unsubscriptionBetbookEndpoint, {
			eventIds: ledgerData.events,
			marketTypes: ledgerData.markets,
			countryCode: settings.getCountryConfig?.countryIsoTwo,
		});
	}
};

const getMarketTypesParams = (marketTypes: string[]) => {
	let marketTypesParams = "";
	if (marketTypes.length > 0) {
		for (let index = 0; index < marketTypes.length; index++) {
			marketTypesParams += "&marketTypes=" + encodeURIComponent(marketTypes[index]);
		}
		return marketTypesParams;
	}
	return "";
};

const getFilterQueryParams = () => {
	let queryParams = "";
	if (reactiveFilters.value.odds) {
		queryParams += `&Odds.Minimum=${reactiveFilters.value.odds?.minimum}&Odds.Maximum=${reactiveFilters.value.odds?.maximum}`;
	}

	if (reactiveFilters.value.toStartEpoch) {
		queryParams += `&ToStartEpoch=${reactiveFilters.value.toStartEpoch}`;
	}

	if (reactiveFilters.value.fromStartEpoch) {
		queryParams += `&FromStartEpoch=${reactiveFilters.value.fromStartEpoch.toFixed()}`;
	}

	if (reactiveFilters.value.sortOrder) {
		queryParams += `&SortOrder=${reactiveFilters.value.sortOrder}`;
	}

	return queryParams;
};

const getRegionsAndLeagues = (
	regionAndLeagues: {
		regionId: string;
		leagueId: string;
	}[]
) => {
	let queryParams = "";
	for (let index = 0; index < regionAndLeagues.length; index++) {
		const regionLeague = regionAndLeagues[index];
		queryParams +=
			"&RegionAndLeagueIds[" + index + "].regionId=" + regionLeague.regionId + "&RegionAndLeagueIds[" + index + "].leagueId=" + regionLeague.leagueId;
	}
	return queryParams;
};

const getBetbookUrl = (marketTypes: string[]) => {
	let fixture = "";
	let queryParams = "";
	switch (filterApplied.value ? 3 : fixtureTypeMap[props.fixtureType || 1]) {
		case 0:
			fixture = "LiveInPlay";
			queryParams = `countryCode=${settings.getCountryConfig?.countryIsoTwo}&sportId=${props.sport?.sportId}&Skip=${reactiveFilters.value.paging.skip}&Take=${reactiveFilters.value.paging.take}&cultureCode=${settings.locale}&isEsport=${props.isEsports}&boostedOnly=${reactiveFilters.value.boostedOnly}`;
			queryParams += getMarketTypesParams(marketTypes);
			break;
		case 1:
			fixture = "Highlights";
			queryParams = `countryCode=${settings.getCountryConfig?.countryIsoTwo}&sportId=${props.sport?.sportId}&Skip=${reactiveFilters.value.paging.skip}&Take=${reactiveFilters.value.paging.take}&cultureCode=${settings.locale}&isEsport=${props.isEsports}&boostedOnly=${reactiveFilters.value.boostedOnly}`;
			queryParams += getMarketTypesParams(marketTypes);
			break;
		case 2:
			fixture = "Upcoming";
			queryParams = `countryCode=${settings.getCountryConfig?.countryIsoTwo}&sportId=${props.sport?.sportId}&Skip=${reactiveFilters.value.paging.skip}&Take=${reactiveFilters.value.paging.take}&cultureCode=${settings.locale}&isEsport=${props.isEsports}&boostedOnly=${reactiveFilters.value.boostedOnly}`;
			queryParams += getMarketTypesParams(marketTypes);
			break;
		case 4:
			fixture = "Outrights";
			queryParams = `countryCode=${settings.getCountryConfig?.countryIsoTwo}&sportId=${props.sport?.sportId}&Skip=${reactiveFilters.value.paging.skip}&Take=${reactiveFilters.value.paging.take}&cultureCode=${settings.locale}&isEsport=${props.isEsports}&boostedOnly=${reactiveFilters.value.boostedOnly}`;
			break;
		case 3:
			fixture = "Filtered";
			queryParams = `countryCode=${settings.getCountryConfig?.countryIsoTwo}&sportId=${props.sport?.sportId}&Skip=${
				reactiveFilters.value.paging.skip
			}&Take=${(reactiveFilters.value.paging.take || 20) * (reactiveFilters.value.regionAndLeagueIds?.length || 1)}&cultureCode=${
				settings.locale
			}&isEsport=${props.isEsports}&boostedOnly=${reactiveFilters.value.boostedOnly}`;
			queryParams += getFilterQueryParams();
			queryParams += getMarketTypesParams(marketTypes);

			if (reactiveFilters.value.regionAndLeagueIds) {
				queryParams += getRegionsAndLeagues(reactiveFilters.value.regionAndLeagueIds);
			}
			break;
		default:
			fixture = "Highlights";
			queryParams = `countryCode=${settings.getCountryConfig?.countryIsoTwo}&sportId=${props.sport?.sportId}&Skip=${reactiveFilters.value.paging.skip}&Take=${reactiveFilters.value.paging.take}&cultureCode=${settings.locale}&isEsport=${props.isEsports}&boostedOnly=${reactiveFilters.value.boostedOnly}`;
			queryParams += getMarketTypesParams(marketTypes);
			break;
	}

	return `/BetBook/${fixture}/?${queryParams}`;
};

const handleSubScription = async (data: IBetbookCall, changedMarkets?: string[]) => {
	const active = data.events.map((e) => e.eventId);
	await $checkSignalRConnectionStatus("Sports");
	if (store.events.size) handleSignalRunsubscribe();
	const subMarkets = changedMarkets
		? [...new Set([...reactiveDisplayMarkets.value.map((m) => m.name), ...changedMarkets])]
		: reactiveDisplayMarkets.value.map((m) => m.name);
	betbookSignalR.subscribe(
		signalREndpoints.subscriptionBetbookEndpoint,
		active,
		{
			eventIds: active,
			marketTypes: subMarkets,
			countryCode: settings.getCountryConfig?.countryIsoTwo,
		},
		subMarkets
	);
};

const betbookCall = (excludeRegions?: boolean, changedMarkets?: string[]) => {
	return api<IBetbookCall>({
		url: getBetbookUrl(
			changedMarkets
				? changedMarkets.map((m) => m)
				: settings.simpleView
				? [reactiveFilters.value.markets[0].name]
				: [
						...reactiveFilters.value.markets.map((x) => x.name),
						...(totalGoalsIsActive.value ? ["[Total Goals]", ...Object.values(totalGoalMarkets).map((x) => x.name)] : []),
				  ]
		),
		type: "sports",
		apiVersion: "v1",
	}).then(async (data) => {
		try {
			const keepExisting = !!changedMarkets?.length;

			if (!keepExisting) {
				await Promise.all([
					(store.events = new Map()),
					(store.markets = new Map()),
					(store.outcomes = new Map()),
					(store.prices = new Map()),
					(store.scores = new Map()),
				]);
			}

			await Promise.all([
				store.setEvents(data.events, keepExisting),
				store.setMarkets(data.markets, keepExisting),
				store.setOutcomes(data.outcomes, keepExisting),
				store.setPrices(data.prices, keepExisting),
				store.setScores(data.scores, keepExisting),
				// store.setRegions(data.regions, keepExisting),
			]);

			store.pointers = {
				events: [],
				markets: new Map(),
				outcomes: new Map(),
			};
			store.createEmopPointers();
			handleSubScription(data, changedMarkets);
		} catch (e) {}
		return data;
	});
};

const {
	data: betbookData,
	isLoading,
	isFetching: isFetchingMoreEvents,
} = useQuery<IBetbookCall>({
	queryKey: ["betbook", props.sport?.sportId, props.fixtureType, reactiveFilters, filterApplied],
	enabled: shouldFetchBook,
	refetchOnWindowFocus: false,
	refetchOnReconnect: false,
	cacheTime: 0,
	keepPreviousData: props.fixtureType === "upcoming" && reactiveFilters.value.paging.skip > 0,
	queryFn: () => betbookCall(),
});

onBeforeUnmount(() => {
	handleSignalRunsubscribe();
});

const handleReconnect = async () => {
	if ((!isLoading.value || isFetchingMoreEvents.value) && betbookData.value) {
		await handleSignalRunsubscribe();
		betbookCall(true);
	}
};

useSignalRRecconect(handleReconnect);

watch(
	() => reactiveDisplayMarkets.value,
	(newVal, oldValue) => {
		if (oldValue !== newVal && betbookData.value && !store.applyingFilters) {
			const oldMarketNames = oldValue.map((m) => m.name);
			const newMarkets = newVal.map((m) => m.name);
			const changedMarkets = newMarkets.filter((m) => !oldMarketNames.includes(m));
			if (reactiveDisplayMarkets.value.length && totalGoalsIsActive.value) {
				betbookCall(true, [...changedMarkets, "[Total Goals]", ...Object.values(totalGoalMarkets).map((x) => x.name)]);
			} else {
				betbookCall(true, changedMarkets);
			}
		}
	}
);
</script>

<template>
	<SportsBookOutrights v-if="fixtureType === 'outrights'" :isLoading="isLoading" :regions="betbookData?.outrightRegions" />
	<SportsBook
		v-else
		:filterApplied="filterApplied"
		:isEsports="isEsports"
		:isLoading="isLoading"
		:fixture-type="fixtureType"
		:isFetchingMoreEvents="isFetchingMoreEvents"
		:more-events="!betbookData?.isFinalPage"
		:key="`${sport?.sportId}-${fixtureType}`"
	/>
</template>
